// src/public/tests/LogicAbstractTest.js
import { useState, useEffect } from 'react';
import {
    Paper, Box, Typography, Button, LinearProgress,
    RadioGroup, FormControlLabel, Radio, Stack, Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const logicalAbstractQuestionsES = [
    { id: 1, question: "Perros, ratones, animales", correctAnswer: "A" },
    { id: 2, question: "Árboles, raíces comestibles, zanahorias", correctAnswer: "E" },
    { id: 3, question: "Champiñones, frutos del bosque, productos alimenticios", correctAnswer: "A" },
    { id: 4, question: "Edificios, palacios, residencias oficiales blancas", correctAnswer: "B" },
    { id: 5, question: "Objetos afilados, cubiertos, pelotas de caucho", correctAnswer: "E" },
    { id: 6, question: "Objetos parcialmente amarillos, casas, imágenes", correctAnswer: "C" },
    { id: 7, question: "Cadenas, joyas, camas", correctAnswer: "E" },
    { id: 8, question: "Carros, BMW’s, Toyota’s", correctAnswer: "A" },
    { id: 9, question: "Pollos, atún, pescado", correctAnswer: "E" },
    { id: 10, question: "Robles, árboles, arces", correctAnswer: "A" },
    { id: 11, question: "Criaturas voladoras, insectos, ratones", correctAnswer: "D" },
    { id: 12, question: "Médicos, jugadores de golf, personas", correctAnswer: "B" },
    { id: 13, question: "Carros rojos, BMW’s, vehículos de motor", correctAnswer: "B" },
    { id: 14, question: "Sillas, muebles, ventanas", correctAnswer: "E" },
    { id: 15, question: "Elementos voladores, animales, vehículos", correctAnswer: "C" },
    { id: 16, question: "Camisetas, objetos de algodón, objetos azules", correctAnswer: "C" },
    { id: 17, question: "Pinos, árboles, rocas", correctAnswer: "E" },
    { id: 18, question: "Piedras preciosas, diamantes, objetos duros", correctAnswer: "B" },
    { id: 19, question: "Pantalones, camisetas, sombreros", correctAnswer: "A" },
    { id: 20, question: "Sillas, líquidos, cañas de pescar", correctAnswer: "A" },
    { id: 21, question: "Perros, osos, animales blancos", correctAnswer: "C" },
    { id: 22, question: "Objetos pintados de rojo, carros, bicicletas", correctAnswer: "D" },
    { id: 23, question: "Tormentas, malas condiciones del clima, tornados/huracanes", correctAnswer: "B" },
    { id: 24, question: "Papas, ratones, animales", correctAnswer: "E" },
    { id: 25, question: "Objetos para poner la mesa, cubiertos, cuchillos", correctAnswer: "B" },
    { id: 26, question: "Perros, poodles, animales", correctAnswer: "B" },
    { id: 27, question: "Árboles, tomates, granos", correctAnswer: "A" },
    { id: 28, question: "Tonos rojizos, colores, tamaños", correctAnswer: "E" },
    { id: 29, question: "Mascotas, conejos, animales blancos", correctAnswer: "C" },
    { id: 30, question: "Canastas, sombreros, objetos hechos con paja", correctAnswer: "D" }
]

const logicalAbstractQuestionsEN = [
    { id: 1, question: "Dogs, mice, animals", correctAnswer: "A" },
    { id: 2, question: "Trees, edible roots, carrots", correctAnswer: "E" },
    { id: 3, question: "Mushrooms, forest fruits, food products", correctAnswer: "A" },
    { id: 4, question: "Buildings, palaces, white official residences", correctAnswer: "B" },
    { id: 5, question: "Sharp objects, cutlery, rubber balls", correctAnswer: "E" },
    { id: 6, question: "Partially yellow objects, houses, images", correctAnswer: "C" },
    { id: 7, question: "Chains, jewelry, beds", correctAnswer: "E" },
    { id: 8, question: "Cars, BMWs, Toyotas", correctAnswer: "A" },
    { id: 9, question: "Chickens, tuna, fish", correctAnswer: "E" },
    { id: 10, question: "Oaks, trees, maples", correctAnswer: "A" },
    { id: 11, question: "Flying creatures, insects, mice", correctAnswer: "D" },
    { id: 12, question: "Doctors, golfers, people", correctAnswer: "B" },
    { id: 13, question: "Red cars, BMWs, motor vehicles", correctAnswer: "B" },
    { id: 14, question: "Chairs, furniture, windows", correctAnswer: "E" },
    { id: 15, question: "Flying elements, animals, vehicles", correctAnswer: "C" },
    { id: 16, question: "T-shirts, cotton objects, blue objects", correctAnswer: "C" },
    { id: 17, question: "Pines, trees, rocks", correctAnswer: "E" },
    { id: 18, question: "Gemstones, diamonds, hard objects", correctAnswer: "B" },
    { id: 19, question: "Pants, T-shirts, hats", correctAnswer: "A" },
    { id: 20, question: "Chairs, liquids, fishing rods", correctAnswer: "A" },
    { id: 21, question: "Dogs, bears, white animals", correctAnswer: "C" },
    { id: 22, question: "Objects painted red, cars, bicycles", correctAnswer: "D" },
    { id: 23, question: "Storms, bad weather conditions, tornadoes/hurricanes", correctAnswer: "B" },
    { id: 24, question: "Potatoes, mice, animals", correctAnswer: "E" },
    { id: 25, question: "Tableware, cutlery, knives", correctAnswer: "B" },
    { id: 26, question: "Dogs, poodles, animals", correctAnswer: "B" },
    { id: 27, question: "Trees, tomatoes, grains", correctAnswer: "A" },
    { id: 28, question: "Reddish tones, colors, sizes", correctAnswer: "E" },
    { id: 29, question: "Pets, rabbits, white animals", correctAnswer: "C" },
    { id: 30, question: "Baskets, hats, straw-made objects", correctAnswer: "D" }
]

const LogicAbstractTest = ({
    responses,
    onAnswerChange,
    currentQuestion,
    onQuestionChange,
    onComplete,
    saving,
    language = 'es',
    timeRemaining = null
}) => {
    const { t } = useTranslation();

    const totalQuestions = 30;
    const progress = ((currentQuestion + 1) / totalQuestions) * 100;
    const questionNumber = currentQuestion + 1;

    const [selectedOption, setSelectedOption] = useState(null);

    // Verificar si el tiempo expiró
    const isTimeExpired = timeRemaining !== null && timeRemaining <= 0;

    // Seleccionar banco según idioma
    const qaBank = language === 'en' ? logicalAbstractQuestionsEN : logicalAbstractQuestionsES;

    // Obtener el objeto de la pregunta actual (ids 1..30 → índice 0..29)
    const currentQ = qaBank[questionNumber - 1];
    const questionText = currentQ?.question ?? '';


    // Cargar respuesta guardada cuando cambia la pregunta
    useEffect(() => {
        setSelectedOption(responses[questionNumber] ?? null);
    }, [currentQuestion, questionNumber, responses]);

    // Cargar imágenes dinámicamente (igual que ya tienes)
    const imagesCtx = require.context('../assets/logic-abstract', false, /\.(jpg|jpeg|png)$/);
    const imageIndex = imagesCtx.keys().reduce((acc, key) => {
        const base = key.replace('./', '').replace(/\.(jpg|jpeg|png)$/, ''); // p.ej: "1-15A"
        acc[base] = imagesCtx(key);
        return acc;
    }, {});

    // Prefijo según el número de pregunta
    const getPrefixForQuestion = (qNum) => (qNum <= 15 ? '1-15' : '16-30');

    // Determinar qué imágenes mostrar según el número de pregunta
    const getImagesForQuestion = (qNum) => {
        const options = ['A', 'B', 'C', 'D', 'E'];
        const prefix = getPrefixForQuestion(qNum); // "1-15" o "16-30"

        return options.map((opt) => {
            const imageName = `${prefix}${opt}`; // p.ej: "1-15A" o "16-30C"
            return {
                option: opt,
                imageName,
                imageUrl: imageIndex[imageName] ?? null,
            };
        });
    };

    const images = getImagesForQuestion(questionNumber);


    const handleOptionSelect = (option) => {
        setSelectedOption(option);
        onAnswerChange(questionNumber, option);
    };

    const handleNext = async () => {
        if (currentQuestion < totalQuestions - 1) {
            await onQuestionChange(currentQuestion + 1, responses);
        } else {
            await onComplete(responses);
        }
    };

    const handleSkip = async () => {
        const updatedResponses = { ...responses };
        delete updatedResponses[questionNumber];
        setSelectedOption(null);
        if (currentQuestion < totalQuestions - 1) {
            await onQuestionChange(currentQuestion + 1, updatedResponses);
        } else {
            await onComplete(updatedResponses);
        }
    };

    const canGoNext = selectedOption !== null || isTimeExpired;

    return (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Paper
                elevation={3}
                sx={{
                    p: { xs: 2, md: 4 },
                    borderRadius: 2,
                    width: '80vw)', // ancho grande
                }}
            >
                {/* Barra de progreso */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('reasoningTests.question')} {questionNumber} {t('reasoningTests.of')} {totalQuestions}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {Math.round(progress)}%
                        </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
                </Box>

                {/* Alerta de tiempo */}
                {timeRemaining !== null && timeRemaining < 60 && timeRemaining > 0 && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        {t('reasoningTests.lessThanOneMinute')}
                    </Alert>
                )}

                {/* Alerta de tiempo expirado */}
                {isTimeExpired && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {t('reasoningTests.timeExpired')}
                    </Alert>
                )}

                {/* Título de la pregunta */}
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                    {t('reasoningTests.question')} {questionNumber}
                </Typography>

                {questionText && (
                    <Typography variant="subtitle1" sx={{ mb: 3 }}>
                        {questionText}
                    </Typography>
                )}

                {/* Imágenes con radio buttons */}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: { xs: 2, md: 4 },
                            borderRadius: 2,
                            width: 'min(1200px, 95vw)',  // más ancho, pero sin pasarse
                        }}
                    >
                        <Box sx={{ mb: 4 }}>
                            <RadioGroup
                                value={selectedOption || ''}
                                onChange={(e) => handleOptionSelect(e.target.value)}
                            >
                                {/* Grid 5 columnas que nunca se sale del ancho */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', // 5 columnas fluidas
                                        gap: { xs: 2, md: 3 },
                                        width: '100%',
                                        maxWidth: '100%',
                                        overflow: 'hidden', // evita que algo se salga visualmente
                                        boxSizing: 'border-box',
                                        justifyItems: 'stretch',
                                        alignItems: 'stretch',
                                    }}
                                >
                                    {images.map(({ option, imageUrl, imageName }) => {
                                        const isSelected = selectedOption === option;

                                        return (
                                            <Box
                                                key={option}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'space-between',
                                                    borderWidth: isSelected ? 3 : 1,
                                                    borderStyle: 'solid',
                                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                                    borderRadius: 2,
                                                    p: 2,
                                                    cursor: isTimeExpired ? 'not-allowed' : 'pointer',
                                                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                                    backgroundColor: 'transparent',
                                                    boxShadow: isSelected ? 2 : 0,
                                                    '&:hover': !isTimeExpired
                                                        ? { borderColor: 'primary.main', boxShadow: 2 }
                                                        : {},
                                                }}
                                                onClick={() => !isTimeExpired && handleOptionSelect(option)}
                                            >
                                                {imageUrl ? (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            minHeight: { xs: 140, sm: 160, md: 200 },
                                                            maxHeight: { xs: 180, sm: 220, md: 260 },
                                                            flexGrow: 1,
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            mb: 2,
                                                        }}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src={imageUrl}
                                                            alt={`Opción ${option}`}
                                                            sx={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'contain',
                                                                borderRadius: 1,
                                                            }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                           no img: {imageName}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                <FormControlLabel
                                                    value={option}
                                                    control={
                                                        <Radio
                                                            disabled={isTimeExpired}
                                                            sx={{
                                                                color: 'text.secondary',
                                                                '&.Mui-checked': { color: 'primary.main' },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="body1">
                                                            <strong>{option}</strong>
                                                        </Typography>
                                                    }
                                                    sx={{ display: 'flex', justifyContent: 'center', m: 0, pt: 1 }}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </RadioGroup>
                        </Box>
                    </Paper>
                </Box>


                {/* Botones de navegación */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button
                        variant="text"
                        onClick={handleSkip}
                        disabled={saving || isTimeExpired}
                    >
                        {t('reasoningTests.skipQuestion')}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={!canGoNext || saving}
                        size="large"
                    >
                        {currentQuestion === totalQuestions - 1
                            ? t('reasoningTests.finish')
                            : t('reasoningTests.next')
                        }
                    </Button>
                </Box>

                {/* Indicador de guardado */}
                {saving && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', textAlign: 'center', mt: 2 }}
                    >
                        {t('reasoningTests.savingProgress')}
                    </Typography>
                )}
            </Paper>
        </Box>
    );
};

export default LogicAbstractTest;